import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { adminApiHeader, finalRouteApi } from '../../components/CommonApis/CommonApiUrl';
import { Link } from 'react-router-dom';
import { FaChartBar, FaList, FaPlus, FaCog, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { IoMdStats } from 'react-icons/io';
import { BsCardChecklist } from 'react-icons/bs';
import FormContainer from '../../components/CommonComponents/FormContainer';
import Wrapper from '../../components/CommonComponents/Wrapper';
import MessageHeading from '../../components/CommonComponents/MessageHeading';
import './Dashboard.css';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalFields: 0,
    activeFields: 0,
    totalAssignments: 0,
    fieldsByType: {},
    fieldsByPage: {},
    totalSets: 0,
    totalValues: 0,
    recentFields: []
  });
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(
        `${finalRouteApi}/custom-fields/dashboard`,
        { headers: adminApiHeader }
      );
      
      setStats(response.data.stats || {});
      setEnabled(response.data.enabled || false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      await axios.patch(
        `${finalRouteApi}/custom-fields/toggle`,
        { enabled: !enabled },
        { headers: adminApiHeader }
      );
      setEnabled(!enabled);
      
      // Refresh dashboard data after toggle
      if (!enabled) {
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error toggling custom fields:', error);
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <FormContainer>
        <Wrapper>
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </Wrapper>
      </FormContainer>
    );
  }

  const fieldTypeColors = {
    text: '#3498db',
    textarea: '#9b59b6',
    select: '#e74c3c',
    multiselect: '#e67e22',
    checkbox: '#2ecc71',
    radio: '#f39c12',
    number: '#34495e',
    date: '#16a085',
    file: '#8e44ad'
  };

  return (
    <FormContainer>
      <Wrapper>
        <div className="custom-fields-dashboard">
          <div className="dashboard-header">
            <MessageHeading>
              <IoMdStats className="mr-2" /> Custom Fields Dashboard
            </MessageHeading>
            <button
              className={`btn ${enabled ? 'btn-success' : 'btn-secondary'}`}
              onClick={handleToggle}
              disabled={toggling}
            >
              {toggling ? (
                <span className="spinner-border spinner-border-sm mr-2" />
              ) : enabled ? (
                <FaToggleOn className="mr-2" />
              ) : (
                <FaToggleOff className="mr-2" />
              )}
              {enabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {!enabled && (
            <div className="alert alert-warning mt-3">
              <strong>Custom Fields is currently disabled.</strong> Enable it to start using custom fields across your application.
            </div>
          )}

          {enabled && (
            <>
              {/* Quick Actions */}
              <div className="quick-actions">
                <Link to="/custom-fields" className="action-card">
                  <FaList />
                  <span>Manage Fields</span>
                </Link>
                <Link to="/custom-fields/create" className="action-card">
                  <FaPlus />
                  <span>Add Field</span>
                </Link>
                <Link to="/custom-fields/assign" className="action-card">
                  <BsCardChecklist />
                  <span>Assign Fields</span>
                </Link>
                <Link to="/custom-fields/conditions" className="action-card">
                  <FaCog />
                  <span>Conditions</span>
                </Link>
              </div>

              {/* Statistics Cards */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: '#3498db' }}>
                    <FaList />
                  </div>
                  <div className="stat-content">
                    <h3>{stats.totalFields || 0}</h3>
                    <p>Total Fields</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: '#2ecc71' }}>
                    <FaToggleOn />
                  </div>
                  <div className="stat-content">
                    <h3>{stats.activeFields || 0}</h3>
                    <p>Active Fields</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: '#e74c3c' }}>
                    <BsCardChecklist />
                  </div>
                  <div className="stat-content">
                    <h3>{stats.totalAssignments || 0}</h3>
                    <p>Assignments</p>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: '#9b59b6' }}>
                    <FaChartBar />
                  </div>
                  <div className="stat-content">
                    <h3>{stats.totalValues || 0}</h3>
                    <p>Values Stored</p>
                  </div>
                </div>
              </div>

              {/* Field Types Distribution */}
              <div className="dashboard-row">
                <div className="dashboard-card">
                  <h4>Fields by Type</h4>
                  <div className="field-types-chart">
                    {Object.entries(stats.fieldsByType || {}).map(([type, count]) => (
                      <div key={type} className="type-bar">
                        <div className="type-label">{type}</div>
                        <div className="type-progress">
                          <div
                            className="type-fill"
                            style={{
                              width: `${(count / stats.totalFields) * 100}%`,
                              backgroundColor: fieldTypeColors[type] || '#95a5a6'
                            }}
                          >
                            {count}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dashboard-card">
                  <h4>Fields by Page</h4>
                  <div className="page-assignments">
                    {Object.entries(stats.fieldsByPage || {}).length > 0 ? (
                      Object.entries(stats.fieldsByPage).map(([page, count]) => (
                        <div key={page} className="page-item">
                          <span className="page-name">{page}</span>
                          <span className="page-count badge badge-primary">{count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted">No field assignments yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Fields */}
              <div className="dashboard-card">
                <h4>Recently Added Fields</h4>
                <div className="recent-fields">
                  {stats.recentFields && stats.recentFields.length > 0 ? (
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Key</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recentFields.map(field => (
                          <tr key={field.id}>
                            <td>{field.name}</td>
                            <td><code>{field.key}</code></td>
                            <td>
                              <span className="badge badge-secondary">{field.type}</span>
                            </td>
                            <td>
                              {field.active ? (
                                <span className="badge badge-success">Active</span>
                              ) : (
                                <span className="badge badge-danger">Inactive</span>
                              )}
                            </td>
                            <td>{new Date(field.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted">No fields created yet</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Wrapper>
    </FormContainer>
  );
}
